import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  styled,
  Select,
  MenuItem
} from '@mui/material';
import { useSelector } from 'react-redux';
import ConfirmationModal from './ConfirmationModal';
import { useDispatch } from 'react-redux';
import { takeItOut } from '../../../../store/slices/viewingSlice';
import { selectAccount } from '../../../../store/slices/accountSlice';
import { useTranslation } from 'react-i18next';

const OptionBox = styled(Box)(({ theme, selected }) => ({
  width: '100%',
  height: '100px',
  border: `1px solid ${selected ? theme.palette.primary.main : '#E5E7EB'}`,
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: selected ? '#F5F8FF' : 'transparent',
  '&:hover': {
    borderColor: theme.palette.primary.main
  }
}));

const TakeItOutModal = ({ open, handleClose, setIsAfterViewings }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState('immediately');
  const [viewingsCount, setViewingsCount] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { data: userData } = useSelector(selectAccount);

  const handleOptionChange = (option) => {
    setSelectedOption(option);
  };

  const handleViewingsCountChange = (event) => {
    setViewingsCount(event.target.value);
  };

  const handleTakeItOut = () => {
    const body = {
      number: viewingsCount,
      type: selectedOption,
      user_id: userData.id
    };
    dispatch(takeItOut(body));
    handleClose();
    setConfirmationOpen(true);
  };

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmTakeOut = () => {
    console.log('Ad taken out:', { selectedOption, viewingsCount });
    // The modal will now show the success message, so we don't close it here
  };

  const viewingsOptions = Array.from({ length: 20 }, (_, i) => i + 1);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="details-modal-title"
        aria-describedby="details-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 560,
            minHeight: '200px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 0,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 40px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.15)'
            }}
          >
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: '700',
                lineHeight: '26px',
                color: '#202020'
              }}
            >
              {t('Take it out')}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: '24px 40px',
              overflowY: 'auto',
              maxHeight: '600px',
              flexGrow: 1
            }}
          >
            <Box sx={{ display: 'grid', gap: '24px' }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: '500',
                  lineHeight: '24px',
                  color: '#202020'
                }}
              >
                {t('Select an option')}
              </Typography>
              <FormControl component="fieldset">
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <OptionBox
                    selected={selectedOption === 'immediately'}
                    onClick={() => handleOptionChange('immediately')}
                    sx={{ padding: '32px 24px', textAlign: 'center' }}
                  >
                    <Box
                      sx={{
                        background: '#EDF2FE',
                        width: '40px',
                        height: '40px',
                        padding: '12px',
                        borderRadius: '40px'
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <path
                          d="M33.75 7.8125H6.25C6.00136 7.8125 5.7629 7.91127 5.58709 8.08709C5.41127 8.2629 5.3125 8.50136 5.3125 8.75C5.3125 8.99864 5.41127 9.2371 5.58709 9.41291C5.7629 9.58873 6.00136 9.6875 6.25 9.6875H7.8125V32.5C7.8125 33.0802 8.04297 33.6366 8.4532 34.0468C8.86344 34.457 9.41984 34.6875 10 34.6875H30C30.5802 34.6875 31.1366 34.457 31.5468 34.0468C31.957 33.6366 32.1875 33.0802 32.1875 32.5V9.6875H33.75C33.9986 9.6875 34.2371 9.58873 34.4129 9.41291C34.5887 9.2371 34.6875 8.99864 34.6875 8.75C34.6875 8.50136 34.5887 8.2629 34.4129 8.08709C34.2371 7.91127 33.9986 7.8125 33.75 7.8125ZM30.3125 32.5C30.3125 32.5829 30.2796 32.6624 30.221 32.721C30.1624 32.7796 30.0829 32.8125 30 32.8125H10C9.91712 32.8125 9.83763 32.7796 9.77903 32.721C9.72042 32.6624 9.6875 32.5829 9.6875 32.5V9.6875H30.3125V32.5ZM12.8125 3.75C12.8125 3.50136 12.9113 3.2629 13.0871 3.08709C13.2629 2.91127 13.5014 2.8125 13.75 2.8125H26.25C26.4986 2.8125 26.7371 2.91127 26.9129 3.08709C27.0887 3.2629 27.1875 3.50136 27.1875 3.75C27.1875 3.99864 27.0887 4.2371 26.9129 4.41291C26.7371 4.58873 26.4986 4.6875 26.25 4.6875H13.75C13.5014 4.6875 13.2629 4.58873 13.0871 4.41291C12.9113 4.2371 12.8125 3.99864 12.8125 3.75Z"
                          fill="#202020"
                        />
                      </svg>
                    </Box>
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        color: '#202020'
                      }}
                    >
                      {t('Immediately')}
                    </Typography>
                  </OptionBox>
                  <OptionBox
                    selected={selectedOption === 'after-viewings'}
                    onClick={() => handleOptionChange('after-viewings')}
                    sx={{ padding: '32px 24px', textAlign: 'center' }}
                  >
                    <Box
                      sx={{
                        background: '#EDF2FE',
                        width: '40px',
                        height: '40px',
                        padding: '12px',
                        borderRadius: '40px'
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <path
                          d="M32.5 5.3125H28.4375V3.75C28.4375 3.50136 28.3387 3.2629 28.1629 3.08709C27.9871 2.91127 27.7486 2.8125 27.5 2.8125C27.2514 2.8125 27.0129 2.91127 26.8371 3.08709C26.6613 3.2629 26.5625 3.50136 26.5625 3.75V5.3125H13.4375V3.75C13.4375 3.50136 13.3387 3.2629 13.1629 3.08709C12.9871 2.91127 12.7486 2.8125 12.5 2.8125C12.2514 2.8125 12.0129 2.91127 11.8371 3.08709C11.6613 3.2629 11.5625 3.50136 11.5625 3.75V5.3125H7.5C6.91984 5.3125 6.36344 5.54297 5.9532 5.9532C5.54297 6.36344 5.3125 6.91984 5.3125 7.5V32.5C5.3125 33.0802 5.54297 33.6366 5.9532 34.0468C6.36344 34.457 6.91984 34.6875 7.5 34.6875H32.5C33.0802 34.6875 33.6366 34.457 34.0468 34.0468C34.457 33.6366 34.6875 33.0802 34.6875 32.5V7.5C34.6875 6.91984 34.457 6.36344 34.0468 5.9532C33.6366 5.54297 33.0802 5.3125 32.5 5.3125ZM7.5 7.1875H11.5625V8.75C11.5625 8.99864 11.6613 9.2371 11.8371 9.41291C12.0129 9.58873 12.2514 9.6875 12.5 9.6875C12.7486 9.6875 12.9871 9.58873 13.1629 9.41291C13.3387 9.2371 13.4375 8.99864 13.4375 8.75V7.1875H26.5625V8.75C26.5625 8.99864 26.6613 9.2371 26.8371 9.41291C27.0129 9.58873 27.2514 9.6875 27.5 9.6875C27.7486 9.6875 27.9871 9.58873 28.1629 9.41291C28.3387 9.2371 28.4375 8.99864 28.4375 8.75V7.1875H32.5C32.5829 7.1875 32.6624 7.22042 32.721 7.27903C32.7796 7.33763 32.8125 7.41712 32.8125 7.5V12.8125H7.1875V7.5C7.1875 7.41712 7.22042 7.33763 7.27903 7.27903C7.33763 7.22042 7.41712 7.1875 7.5 7.1875ZM32.5 32.8125H7.5C7.41712 32.8125 7.33763 32.7796 7.27903 32.721C7.22042 32.6624 7.1875 32.5829 7.1875 32.5V14.6875H32.8125V32.5C32.8125 32.5829 32.7796 32.6624 32.721 32.721C32.6624 32.7796 32.5829 32.8125 32.5 32.8125ZM24.4125 20.6625L21.325 23.75L24.4125 26.8375C24.5046 26.9233 24.5785 27.0268 24.6297 27.1418C24.681 27.2568 24.7085 27.381 24.7107 27.5068C24.713 27.6327 24.6898 27.7578 24.6427 27.8745C24.5955 27.9912 24.5253 28.0973 24.4363 28.1863C24.3473 28.2753 24.2412 28.3455 24.1245 28.3927C24.0078 28.4398 23.8827 28.463 23.7568 28.4607C23.631 28.4585 23.5068 28.431 23.3918 28.3797C23.2768 28.3285 23.1733 28.2546 23.0875 28.1625L20 25.075L16.9125 28.1625C16.7348 28.3281 16.4997 28.4183 16.2568 28.414C16.014 28.4097 15.7822 28.3113 15.6105 28.1395C15.4387 27.9678 15.3403 27.736 15.336 27.4932C15.3317 27.2503 15.4219 27.0152 15.5875 26.8375L18.675 23.75L15.5875 20.6625C15.4954 20.5767 15.4215 20.4732 15.3703 20.3582C15.319 20.2432 15.2915 20.119 15.2893 19.9932C15.287 19.8673 15.3102 19.7422 15.3573 19.6255C15.4045 19.5088 15.4747 19.4027 15.5637 19.3137C15.6527 19.2247 15.7588 19.1545 15.8755 19.1073C15.9922 19.0602 16.1173 19.037 16.2432 19.0393C16.369 19.0415 16.4932 19.069 16.6082 19.1203C16.7232 19.1715 16.8267 19.2454 16.9125 19.3375L20 22.425L23.0875 19.3375C23.1733 19.2454 23.2768 19.1715 23.3918 19.1203C23.5068 19.069 23.631 19.0415 23.7568 19.0393C23.8827 19.037 24.0078 19.0602 24.1245 19.1073C24.2412 19.1545 24.3473 19.2247 24.4363 19.3137C24.5253 19.4027 24.5955 19.5088 24.6427 19.6255C24.6898 19.7422 24.713 19.8673 24.7107 19.9932C24.7085 20.119 24.681 20.2432 24.6297 20.3582C24.5785 20.4732 24.5046 20.5767 24.4125 20.6625Z"
                          fill="#202020"
                        />
                      </svg>
                    </Box>
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#202020',
                        lineHeight: '24px'
                      }}
                    >
                      {t('After a certain number of viewings')}
                    </Typography>
                  </OptionBox>
                </Box>
                {selectedOption === 'after-viewings' && (
                  <>
                    <Typography
                      sx={{
                        mt: '24px',
                        mb: '10px',
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        color: '#202020'
                      }}
                    >
                      {t('Select a number of viewings')}{' '}
                    </Typography>
                    <Select
                      fullWidth
                      defaultValue=""
                      displayEmpty
                      variant="outlined"
                      size="small"
                      sx={{ flex: 1, height: 40 }}
                      value={viewingsCount}
                      onChange={handleViewingsCountChange}
                    >
                      {viewingsOptions.map((number) => (
                        <MenuItem key={number} value={number.toString()}>
                          {number}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                )}
              </FormControl>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 40px',
              borderTop: '1px solid rgba(0, 0, 0, 0.15)'
            }}
          >
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                border: 'none',
                boxShadow: 'none',
                background: 'none',
                color: '#002BB7C5',
                fontWeight: 400,
                fontSize: '18px',
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none'
                }
              }}
            >
              {t('Cancel')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleTakeItOut}>
              {t('Take it out')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmationModal
        open={confirmationOpen}
        handleClose={handleConfirmationClose}
        handleConfirm={handleConfirmTakeOut}
        selectedOption={selectedOption}
        viewingsCount={viewingsCount}
        setIsAfterViewings={setIsAfterViewings}
      />
    </>
  );
};

export default TakeItOutModal;
